// RFC-0008 T4: 小北宿主通信（精简自 slides/src/app/lib/north-sdk.ts，只保留必需的）
import { useCallback } from 'react';

type PendingEntry = { resolve: (v: unknown) => void };
const pendingCallbacks = new Map<string, PendingEntry>();

function handleMessage(event: MessageEvent) {
  const data = event.data;
  if (!data || typeof data !== 'object') return;
  if (data.type === 'api_call:response' && data.messageId && pendingCallbacks.has(data.messageId)) {
    const { resolve } = pendingCallbacks.get(data.messageId)!;
    pendingCallbacks.delete(data.messageId);
    resolve(data.payload);
  }
}

if (typeof window !== 'undefined' && !(window as any).__northDecksSDKBound) {
  (window as any).__northDecksSDKBound = true;
  window.addEventListener('message', handleMessage);
  if (window.parent !== window) {
    window.parent.postMessage(
      { type: 'runtime:running', source: 'north-app-decks', payload: {} },
      '*',
    );
  }
}

export async function callXiaobeiAPI<T = unknown>(
  apiName: string,
  args: Record<string, unknown>,
  timeoutMs = 15000,
): Promise<{ success: boolean; result?: T; error?: string }> {
  const mid = `${Date.now()}${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve) => {
    pendingCallbacks.set(mid, {
      resolve: (v) => resolve(v as { success: boolean; result?: T; error?: string }),
    });
    window.parent.postMessage(
      {
        type: 'api_call:host',
        source: 'north-app-decks',
        messageId: mid,
        payload: { apiName, args },
      },
      '*',
    );
    setTimeout(() => {
      if (pendingCallbacks.has(mid)) {
        pendingCallbacks.delete(mid);
        resolve({ success: false, error: 'timeout' });
      }
    }, timeoutMs);
  });
}

// ─── Reference / chat ──────────────────────────────────

export async function addReference(
  text: string,
  fileName?: string,
): Promise<{ success: boolean }> {
  const result = await callXiaobeiAPI('addReference', {
    text,
    fileName: fileName ?? '新建 PPT 请求',
  });
  return { success: result.success };
}

export function useAddReference() {
  return useCallback(
    (text: string, fileName?: string) => addReference(text, fileName),
    [],
  );
}

// 把明文一次性写到聊天输入框（用户可见、可编辑、需手动发送）
// 与 addReference 互补：reference 进引用栏卡片，本 API 进文字框
export interface ChatInputReference {
  name: string;
  type: 'skill' | 'app_custom';
  skill_id?: string;
  app_id?: string;
  description?: string;
  data?: Record<string, unknown>;
}

// 把内容注入聊天输入框（可编辑、不自动发送）。
// host（#1533）只认 text；skill/app 引用以 <reference>[...]</reference> 文本
// 形式拼在 text 里，发送后由宿主渲染成 mention 芯片。
export async function setChatInput(
  text: string,
): Promise<{ success: boolean }> {
  const result = await callXiaobeiAPI('setChatInput', { text });
  return { success: result.success };
}

export function useSetChatInput() {
  return useCallback((text: string) => setChatInput(text), []);
}

export function sendChatToHost(message: string): { success: boolean; error?: string } {
  if (typeof window === 'undefined' || window.parent === window) {
    return { success: false, error: 'not in iframe' };
  }
  try {
    window.parent.postMessage(
      { type: 'sendChat', source: 'north-app-decks', payload: { message } },
      '*',
    );
    return { success: true };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}

// 首条消息带 [ppt_user_id:xxx] 前缀，之后自动省略
const FIRST_SENT_KEY = (userId: string) => `decks:first-sent:${userId}`;

export function sendChatWithIdOnce(
  message: string,
  userId: string,
): { success: boolean; error?: string } {
  const alreadySent =
    typeof window !== 'undefined' &&
    window.sessionStorage?.getItem(FIRST_SENT_KEY(userId)) === '1';

  const finalMessage = alreadySent ? message : `[ppt_user_id:${userId}] ${message}`;
  const result = sendChatToHost(finalMessage);

  if (result.success && !alreadySent && typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(FIRST_SENT_KEY(userId), '1');
    } catch {
      // ignore (sessionStorage 不可用)
    }
  }
  return result;
}

// ─── Skill / Artifact ──────────────────────────────────

export interface InstallArtifactResult {
  success: boolean;
  artifactId?: string;
  name?: string;
  version?: string;
  alreadyInstalled?: boolean;
  error?: string;
}

export async function installArtifact(
  name: string,
  options?: { versionTag?: string; workspaceId?: string },
): Promise<InstallArtifactResult> {
  const response = await callXiaobeiAPI<InstallArtifactResult>('installArtifact', {
    name,
    version_tag: options?.versionTag,
    workspace_id: options?.workspaceId,
  });

  if (!response.success) {
    return { success: false, error: response.error ?? 'install failed' };
  }
  const r = (response.result ?? response) as InstallArtifactResult;
  return r;
}

export interface IsInstalledResult {
  success: boolean;
  installed?: boolean;
  artifactId?: string | null;
  error?: string;
}

// 查询当前 workspace 是否已安装该 artifact（只读，真相源）。
// 用于按真实安装状态驱动 UI，而不是依赖 install 调用的返回值——
// 否则用户在别处卸载后，App 无从得知会一直显示已就绪。
// 返回的 artifactId 是当前环境（dev/prod）真实 id，用于构造 skill 引用。
export async function isArtifactInstalled(
  name: string,
  options?: { workspaceId?: string },
): Promise<IsInstalledResult> {
  const response = await callXiaobeiAPI<IsInstalledResult>(
    'isArtifactInstalled',
    {
      name,
      workspace_id: options?.workspaceId,
    },
  );

  if (!response.success) {
    return { success: false, error: response.error ?? 'query failed' };
  }
  const r = (response.result ?? response) as IsInstalledResult;
  return {
    success: true,
    installed: Boolean(r.installed),
    artifactId: r.artifactId ?? null,
  };
}
